// Eyes. Each eye is three layers:
//  - a dark backdrop (the eyeball's inside), so nothing shows through round the iris;
//  - the iris, procedural from the cat's EyeSpec (colour, pupil size and shape), enlarged to fill
//    the eye opening as a cat's iris does;
//  - a clear cornea that adds only reflections (additive black), so the eyes catch the light.
// In a catnip trip the iris becomes a blank, glowing pale disc, as in the site's _trip images.
import {
  AdditiveBlending, BackSide, type BufferGeometry, Color, FrontSide, MeshPhysicalMaterial,
  MeshStandardMaterial, type SkinnedMesh, Vector3,
} from 'three';
import type { EyeSpec } from '../../data/cats';
import { NOISE_GLSL } from './coatMaterial';

/** Rest-pose iris centres of the base cat (the left eye; the right is mirrored). */
const IRIS_CENTRE = new Vector3(0.0204, 0.2648, 0.2948);
const CORNEA_CENTRE_Z = 0.2875;
const CORNEA_RADIUS = 0.0105;

function linear(hex: string): Vector3 {
  const c = new Color(hex);
  return new Vector3(c.r, c.g, c.b);
}

/** Enlarges each iris about its own centre. Returns a new geometry. */
export function enlargeIris(geometry: BufferGeometry, scale: number): BufferGeometry {
  const g = geometry.clone();
  const pos = g.getAttribute('position');
  for (let i = 0; i < pos.count; i++) {
    const side = Math.sign(pos.getX(i)) || 1;
    const cx = IRIS_CENTRE.x * side, cy = IRIS_CENTRE.y;
    // Enlarge along the cornea, not out through the eyelids. Scaling the original curved
    // disc in Z made its lower edge protrude and cut triangular notches into the lid.
    const dx = (pos.getX(i) - cx) * scale;
    const dy = (pos.getY(i) - cy) * scale;
    const z = CORNEA_CENTRE_Z + Math.sqrt(Math.max(0, CORNEA_RADIUS ** 2 - dx ** 2 - dy ** 2));
    pos.setXYZ(i, cx + dx, cy + dy, z + 0.00015);
  }
  pos.needsUpdate = true;
  g.computeBoundingSphere();
  return g;
}

export class EyeMaterials {
  readonly iris: MeshStandardMaterial;
  readonly cornea: MeshPhysicalMaterial;
  readonly backdrop: MeshStandardMaterial;
  private uTrip = { value: 0 };
  private uTime = { value: 0 };

  constructor(spec: EyeSpec) {
    const uInner = { value: linear(spec.inner) };
    const uOuter = { value: linear(spec.outer) };
    const uPupil = { value: spec.pupil };
    const uRound = { value: spec.pupilRound };
    const uTrip = this.uTrip;
    const uTime = this.uTime;

    // The iris needs UVs, so it keeps a 1×1 map; the colour is computed from vMapUv.
    this.iris = new MeshStandardMaterial({ roughness: 0.35, metalness: 0 });
    this.iris.defines = { USE_UV: '' };
    this.iris.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, { uInner, uOuter, uPupil, uRound, uTrip, uTime });
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', `#include <common>
          uniform vec3 uInner; uniform vec3 uOuter; uniform float uPupil; uniform float uRound; uniform float uTrip; uniform float uTime;
          ${NOISE_GLSL}`)
        .replace('#include <map_fragment>', `
          vec2 d = (vUv - 0.5) / 0.25;            // the iris disc spans uv 0.25..0.75
          float r = length(d);
          float ang = atan(d.y, d.x);
          float fib = cdNoise3(vec3(cos(ang) * 9.0, sin(ang) * 9.0, r * 3.0)) * 0.6 + cdNoise3(vec3(cos(ang) * 30.0, sin(ang) * 30.0, r * 6.0)) * 0.4;
          vec3 iris = mix(uInner, uOuter, smoothstep(0.25, 0.95, r));
          iris *= 0.7 + 0.6 * fib;
          iris = mix(iris, uInner * 1.4, smoothstep(0.55, 0.3, r) * 0.35);   // a bright collarette round the pupil
          iris *= 1.0 - smoothstep(0.8, 1.0, r) * 0.75;                     // dark limbal ring
          vec2 pd = d / vec2(mix(0.28, 1.0, uRound), 1.0);
          float pupil = 1.0 - smoothstep(uPupil * 0.92, uPupil * 1.04, length(pd));
          vec3 eye = mix(iris, vec3(0.004), pupil);
          // trip: a blank, pale glowing disc
          vec3 trip = vec3(0.92, 0.97, 1.0) * (0.85 + 0.15 * sin(uTime * 6.0 + r * 8.0));
          diffuseColor.rgb = mix(eye, trip, uTrip);`)
        .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
          totalEmissiveRadiance += iris * (1.0 - pupil) * 0.06 * (1.0 - uTrip) + trip * uTrip * 3.2;`);
    };
    this.iris.customProgramCacheKey = () => 'cat-iris';

    this.cornea = new MeshPhysicalMaterial({
      color: 0x000000, roughness: 0.08, metalness: 0, clearcoat: 0.65, clearcoatRoughness: 0.06,
      transparent: true, blending: AdditiveBlending, depthWrite: false, side: FrontSide, envMapIntensity: 0.8,
    });
    const back = new Color(spec.outer).multiplyScalar(0.18);
    this.backdrop = new MeshStandardMaterial({ color: back, roughness: 0.6, side: BackSide });
  }

  setTrip(amount: number, timeS: number): void {
    this.uTrip.value = amount;
    this.uTime.value = timeS;
  }

  get trip(): number { return this.uTrip.value; }

  dispose(): void {
    this.iris.dispose(); this.cornea.dispose(); this.backdrop.dispose();
  }
}

/** Sets up one eyeball mesh as the cornea and adds a backdrop copy behind it. */
export function dressEyeball(mesh: SkinnedMesh, eyes: EyeMaterials): SkinnedMesh {
  mesh.material = eyes.cornea;
  mesh.renderOrder = 2;
  mesh.castShadow = false;
  const back = mesh.clone() as SkinnedMesh;
  back.name = `${mesh.name}_backdrop`;
  back.material = eyes.backdrop;
  back.bind(mesh.skeleton, mesh.bindMatrix);
  back.renderOrder = 0;
  mesh.parent?.add(back);
  return back;
}
